import { Cloud, Github, ScanSearch, Shield, Wrench } from 'lucide-react'
import {
  formatToolValue,
  getToolCallId,
  isToolError,
  normalizeTone,
  pickString,
  plural,
  readToolState,
  ToolDetailOverlayLink,
  ToolListPreview,
  ToolStatusLine,
  type ToolPartProps,
} from './_base'
import { deriveScanStatus, readScanChunk } from './checks-scan-status'

const shieldIcon = <Shield className='h-3.5 w-3.5 shrink-0 text-text-placeholder' />
const fixIcon = <Wrench className='h-3.5 w-3.5 shrink-0 text-text-placeholder' />
const scanIcon = (
  <ScanSearch className='h-3.5 w-3.5 shrink-0 text-text-placeholder' />
)

export function ChecksGetCheckToolMessage({ part }: ToolPartProps) {
  const state = readToolState(part)
  const check = state.result?.check
  return (
    <>
      <ToolStatusLine
        state={state}
        loadingLabel='Looking up check definition...'
        completedLabel='Check definition loaded'
        loadingDetail={pickString(state.args?.checkId)}
        completedDetail={pickString(check?.name, state.args?.checkId)}
        completedIcon={shieldIcon}
      />
      {state.isComplete && !isToolError(state) && check ? (
        <ToolDetailOverlayLink
          overlayId={getToolCallId(
            part,
            `check-${pickString(check.id, state.args?.checkId) ?? 'detail'}`
          )}
          payload={{
            entityType: 'evidence',
            toolName: 'checksGetCheck',
            title: pickString(check.name) ?? 'Check definition',
            subtitle: pickString(check.id, state.args?.checkId),
            description: pickString(check.description),
            fields: [
              { label: 'Provider', value: formatToolValue(check.provider) },
              {
                label: 'Controls',
                value: Array.isArray(check.controlMappings)
                  ? check.controlMappings.join(', ')
                  : null,
              },
              {
                label: 'Slots',
                value: Array.isArray(check.slotKeys)
                  ? check.slotKeys.join(', ')
                  : null,
              },
              {
                label: 'Capabilities',
                value: Array.isArray(check.requiredCapabilities)
                  ? check.requiredCapabilities.join(', ')
                  : null,
              },
            ],
            sections: [
              {
                title: 'Variables',
                content: Array.isArray(check.variables)
                  ? check.variables
                      .map((entry: Record<string, unknown>) =>
                        pickString(entry.name, entry.key, entry.id)
                      )
                      .filter(Boolean)
                      .join('\n')
                  : null,
              },
            ],
          }}
        />
      ) : null}
    </>
  )
}

export function ChecksListChecksToolMessage({ part }: ToolPartProps) {
  const state = readToolState(part)
  const checks: Array<Record<string, unknown>> = Array.isArray(
    state.result?.checks
  )
    ? state.result.checks
    : []
  const count =
    typeof state.result?.total === 'number'
      ? state.result.total
      : typeof state.result?.count === 'number'
        ? state.result.count
        : checks.length
  return (
    <>
      <ToolStatusLine
        state={state}
        loadingLabel='Browsing native checks...'
        completedLabel={
          count !== null ? `Found ${plural(count, 'check')}` : 'Checks loaded'
        }
        completedDetail={pickString(
          state.args?.provider,
          state.args?.frameworkControlRef,
          state.args?.controlRef,
          state.args?.query
        )}
        completedIcon={shieldIcon}
      />
      {state.isComplete && !isToolError(state) ? (
        <ToolListPreview
          emptyLabel='No checks found'
          items={checks.map((check) => ({
            id: null,
            title: pickString(check.name, check.id) ?? 'Unnamed check',
            subtitle: pickString(check.provider),
            meta: [
              Array.isArray(check.controlMappings)
                ? check.controlMappings.slice(0, 2).join(', ')
                : null,
              Array.isArray(check.slotKeys)
                ? check.slotKeys.slice(0, 2).join(', ')
                : null,
            ],
          }))}
        />
      ) : null}
    </>
  )
}

export function ChecksLinkedTestsToolMessage({ part }: ToolPartProps) {
  const state = readToolState(part)
  const tests: Array<Record<string, unknown>> = Array.isArray(
    state.result?.tests
  )
    ? state.result.tests
    : []
  const count =
    typeof state.result?.count === 'number' ? state.result.count : tests.length
  return (
    <>
      <ToolStatusLine
        state={state}
        loadingLabel='Finding linked tests...'
        completedLabel={
          count !== null
            ? `Found ${plural(count, 'linked test')}`
            : 'Linked tests loaded'
        }
        completedDetail={pickString(state.args?.checkId)}
        completedIcon={shieldIcon}
      />
      {state.isComplete && !isToolError(state) ? (
        <ToolListPreview
          emptyLabel='No linked tests'
          items={tests.map((test) => ({
            id: null,
            title:
              pickString(test.templateName, test.title, test.testId) ??
              'Unnamed test',
            subtitle: pickString(test.status),
            meta: [test.assignedTo, test.templateId],
            tone: normalizeTone(test.status),
          }))}
        />
      ) : null}
    </>
  )
}

export function ChecksHistoryToolMessage({ part }: ToolPartProps) {
  const state = readToolState(part)
  const findings: Array<Record<string, unknown>> = Array.isArray(
    state.result?.findings
  )
    ? state.result.findings
    : []
  const count =
    typeof state.result?.count === 'number'
      ? state.result.count
      : findings.length
  return (
    <>
      <ToolStatusLine
        state={state}
        loadingLabel='Reading check history...'
        completedLabel={
          count !== null
            ? `Found ${plural(count, 'finding')}`
            : 'Check history loaded'
        }
        completedDetail={pickString(state.args?.checkId)}
        completedIcon={shieldIcon}
      />
      {state.isComplete && !isToolError(state) ? (
        <ToolListPreview
          emptyLabel='No check history'
          items={findings.map((finding) => ({
            id: null,
            title:
              pickString(finding.title, finding.resourceId, finding.id) ??
              'Scan finding',
            subtitle: pickString(finding.status),
            meta: [finding.severity, finding.resourceType, finding.createdAt],
            tone: normalizeTone(finding.severity),
            detailOverlay: {
              id: getToolCallId(
                part,
                `check-history-${pickString(finding.id) ?? 'detail'}`
              ),
              payload: {
                entityType: 'finding',
                toolName: 'checksHistory',
                title:
                  pickString(finding.title, finding.resourceId) ??
                  'Scan finding',
                subtitle: pickString(finding.id, finding.runId),
                description: pickString(finding.description),
                fields: [
                  {
                    label: 'Status',
                    value: formatToolValue(finding.status),
                    tone: normalizeTone(finding.status),
                  },
                  {
                    label: 'Severity',
                    value: formatToolValue(finding.severity),
                    tone: normalizeTone(finding.severity),
                  },
                  {
                    label: 'Resource',
                    value: pickString(finding.resourceId),
                  },
                  {
                    label: 'Type',
                    value: formatToolValue(finding.resourceType),
                  },
                  { label: 'Run', value: formatToolValue(finding.runId) },
                  {
                    label: 'Created',
                    value: formatToolValue(finding.createdAt),
                  },
                ],
                sections: [
                  {
                    title: 'Remediation',
                    content:
                      typeof finding.remediation === 'string'
                        ? finding.remediation
                        : null,
                  },
                ],
              },
            },
          }))}
        />
      ) : null}
    </>
  )
}

export function ChecksRunToolMessage({ part }: ToolPartProps) {
  const state = readToolState(part)
  const findings: Array<Record<string, unknown>> = Array.isArray(
    state.result?.findings
  )
    ? state.result.findings
    : []
  const summary =
    state.result?.summary && typeof state.result.summary === 'object'
      ? (state.result.summary as Record<string, unknown>)
      : null
  return (
    <>
      <ToolStatusLine
        state={state}
        loadingLabel='Running check...'
        completedLabel='Check complete'
        loadingDetail={pickString(
          state.args?.checkId,
          Array.isArray(state.args?.checkIds)
            ? state.args.checkIds.join(', ')
            : null
        )}
        completedDetail={pickString(
          state.args?.provider,
          state.args?.checkId,
          state.result?.checkId,
          state.result?.runId
        )}
        completedIcon={scanIcon}
      />
      {state.isComplete && !isToolError(state) ? (
        <ToolListPreview
          emptyLabel={
            summary
              ? `Summary: ${Object.entries(summary)
                  .map(([key, value]) => `${key} ${value}`)
                  .join(', ')}`
              : 'No findings returned'
          }
          items={findings.map((finding) => ({
            id: null,
            title:
              pickString(finding.title, finding.checkId, finding.resourceId) ??
              'Check finding',
            subtitle: pickString(finding.status, finding.level),
            meta: [finding.severity, finding.resourceType, finding.resourceId],
            tone: normalizeTone(finding.severity ?? finding.level),
          }))}
        />
      ) : null}
    </>
  )
}

export function ChecksScanToolMessage({ part }: ToolPartProps) {
  const baseState = readToolState(part)
  // Read the latest chunk straight off `part.output` (populated for both
  // preliminary updates and the final result, regardless of how the SDK labels
  // the intermediate `state`), falling back to the parsed terminal result for
  // legacy tool-invocation parts that only carry it there.
  const isPreliminary = Boolean((part as { preliminary?: boolean }).preliminary)
  const chunk = readScanChunk(
    (part as { output?: unknown }).output,
    baseState.result,
  )
  const { state, loadingDetail, completedDetail } = deriveScanStatus({
    chunk,
    isPreliminary,
    baseState,
  })

  return (
    <ToolStatusLine
      state={state}
      loadingLabel='Scanning checks...'
      completedLabel='Check scan complete'
      loadingDetail={loadingDetail}
      completedDetail={completedDetail}
      completedIcon={scanIcon}
    />
  )
}

export function ChecksCloudToolMessage({ part }: ToolPartProps) {
  const state = readToolState(part)
  const findings: Array<Record<string, unknown>> = Array.isArray(
    state.result?.findings
  )
    ? state.result.findings
    : []
  return (
    <>
      <ToolStatusLine
        state={state}
        loadingLabel='Scanning cloud provider...'
        completedLabel='Cloud scan complete'
        loadingDetail={pickString(state.args?.provider, state.args?.checkId)}
        completedDetail={pickString(
          state.args?.provider,
          state.args?.checkId,
          state.result?.runId
        )}
        completedIcon={
          <Cloud className='h-3.5 w-3.5 shrink-0 text-text-placeholder' />
        }
      />
      {state.isComplete && !isToolError(state) ? (
        <ToolListPreview
          emptyLabel='No findings returned'
          items={findings.map((finding) => ({
            id: null,
            title:
              pickString(finding.title, finding.checkId, finding.resourceId) ??
              'Cloud finding',
            subtitle: pickString(finding.status, finding.level),
            meta: [finding.severity, finding.resourceType, finding.resourceId],
            tone: normalizeTone(finding.severity ?? finding.level),
          }))}
        />
      ) : null}
    </>
  )
}

export function ChecksCodeToolMessage({ part }: ToolPartProps) {
  const state = readToolState(part)
  const findings: Array<Record<string, unknown>> = Array.isArray(
    state.result?.findings
  )
    ? state.result.findings
    : Array.isArray(state.result?.results)
      ? state.result.results
      : []
  return (
    <>
      <ToolStatusLine
        state={state}
        loadingLabel='Scanning code repository...'
        completedLabel='Code scan complete'
        loadingDetail={pickString(state.args?.repoPath, state.args?.repo)}
        completedDetail={pickString(
          state.args?.repoPath,
          state.args?.repo,
          state.result?.summary
        )}
        completedIcon={
          <Github className='h-3.5 w-3.5 shrink-0 text-text-placeholder' />
        }
      />
      {state.isComplete && !isToolError(state) ? (
        <ToolListPreview
          emptyLabel='No code findings'
          items={findings.map((finding) => ({
            id: null,
            title:
              pickString(finding.title, finding.message, finding.file) ??
              'Code finding',
            subtitle: pickString(finding.file, finding.path),
            meta: [finding.severity, finding.line, finding.policyName],
            tone: normalizeTone(finding.severity),
          }))}
        />
      ) : null}
    </>
  )
}

export function ChecksSaveFindingToolMessage({ part }: ToolPartProps) {
  const state = readToolState(part)
  return (
    <ToolStatusLine
      state={state}
      loadingLabel='Saving finding...'
      completedLabel='Finding saved'
      completedDetail={pickString(state.args?.checkId)}
      completedIcon={shieldIcon}
    />
  )
}

export function ChecksDetectDriftToolMessage({ part }: ToolPartProps) {
  const state = readToolState(part)
  return (
    <ToolStatusLine
      state={state}
      loadingLabel='Detecting drift...'
      completedLabel='Drift report ready'
      completedDetail={pickString(state.args?.checkId)}
      completedIcon={scanIcon}
    />
  )
}

export function ChecksRemedyPrepareToolMessage({ part }: ToolPartProps) {
  const state = readToolState(part)
  return (
    <ToolStatusLine
      state={state}
      loadingLabel='Preparing remediation...'
      completedLabel='Remediation prepared'
      completedDetail={pickString(state.args?.checkId)}
      completedIcon={fixIcon}
    />
  )
}

export function ChecksRemedyValidateToolMessage({ part }: ToolPartProps) {
  const state = readToolState(part)
  return (
    <ToolStatusLine
      state={state}
      loadingLabel='Validating remediation plan...'
      completedLabel='Remediation plan validated'
      completedDetail={pickString(state.args?.checkId)}
      completedIcon={fixIcon}
    />
  )
}

export function ChecksRemedyExecuteToolMessage({ part }: ToolPartProps) {
  const state = readToolState(part)
  return (
    <ToolStatusLine
      state={state}
      loadingLabel='Executing remediation...'
      completedLabel='Remediation executed'
      completedDetail={pickString(state.args?.checkId)}
      completedIcon={fixIcon}
    />
  )
}

export function ChecksRemedyUndoToolMessage({ part }: ToolPartProps) {
  const state = readToolState(part)
  return (
    <ToolStatusLine
      state={state}
      loadingLabel='Undoing remediation...'
      completedLabel='Remediation undone'
      completedDetail={pickString(state.args?.checkId)}
      completedIcon={fixIcon}
    />
  )
}
